// Localização: ./src/utils/validationUtils.js
// Utilitários para validação de formulários

/**
 * Remove caracteres não numéricos de uma string
 */
export const removeNonNumeric = (str) => {
    if (!str) return '';
    return str.replace(/\D/g, '');
};

/**
 * Valida CPF
 * @param {string} cpf - CPF a ser validado
 * @param {boolean} required - Se true, CPF é obrigatório (padrão: false para manter compatibilidade)
 * @returns {{valid: boolean, message: string}}
 */
export const validateCPF = (cpf, required = false) => {
    if (!cpf || cpf.trim() === '') {
        if (required) {
            return { valid: false, message: 'O campo "CPF" é obrigatório e não pode estar vazio.' };
        }
        return { valid: true, message: '' }; // CPF é opcional quando required = false
    }
    
    const cleanCPF = removeNonNumeric(cpf);
    
    if (cleanCPF.length !== 11) {
        return { valid: false, message: 'CPF deve conter 11 dígitos numéricos.' };
    }
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cleanCPF)) {
        return { valid: false, message: 'CPF inválido. Todos os dígitos são iguais.' };
    }
    
    // Validação do dígito verificador
    let sum = 0;
    let remainder;
    
    for (let i = 1; i <= 9; i++) {
        sum += parseInt(cleanCPF.substring(i - 1, i)) * (11 - i);
    }
    
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.substring(9, 10))) {
        return { valid: false, message: 'CPF inválido. Verifique os dígitos.' };
    }
    
    sum = 0;
    for (let i = 1; i <= 10; i++) {
        sum += parseInt(cleanCPF.substring(i - 1, i)) * (12 - i);
    }
    
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.substring(10, 11))) {
        return { valid: false, message: 'CPF inválido. Verifique os dígitos.' };
    }
    
    return { valid: true, message: '' };
};

/**
 * Formata CPF
 */
export const formatCPF = (cpf) => {
    const cleanCPF = removeNonNumeric(cpf);
    if (cleanCPF.length <= 3) return cleanCPF;
    if (cleanCPF.length <= 6) return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3)}`;
    if (cleanCPF.length <= 9) return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3, 6)}.${cleanCPF.slice(6)}`;
    return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3, 6)}.${cleanCPF.slice(6, 9)}-${cleanCPF.slice(9, 11)}`;
};

/**
 * Valida CEP
 * @param {string} cep - CEP a ser validado
 * @returns {{valid: boolean, message: string}}
 */
export const validateCEP = (cep) => {
    if (!cep) return { valid: true, message: '' }; // CEP é opcional
    
    const cleanCEP = removeNonNumeric(cep);
    
    if (cleanCEP.length !== 8) {
        return { valid: false, message: 'CEP deve conter 8 dígitos numéricos.' };
    }
    
    return { valid: true, message: '' };
};

/**
 * Formata CEP
 */
export const formatCEP = (cep) => {
    const cleanCEP = removeNonNumeric(cep);
    if (cleanCEP.length <= 5) return cleanCEP;
    return `${cleanCEP.slice(0, 5)}-${cleanCEP.slice(5, 8)}`;
};

/**
 * Valida telefone
 * @param {string} telefone - Telefone a ser validado
 * @returns {{valid: boolean, message: string}}
 */
export const validateTelefone = (telefone) => {
    if (!telefone) return { valid: true, message: '' }; // Telefone é opcional
    
    const cleanTelefone = removeNonNumeric(telefone);
    
    // Aceita telefone fixo (10 dígitos) ou celular (11 dígitos)
    if (cleanTelefone.length < 10 || cleanTelefone.length > 11) {
        return { valid: false, message: 'Telefone deve conter 10 ou 11 dígitos numéricos.' };
    }
    
    return { valid: true, message: '' };
};

/**
 * Formata telefone
 */
export const formatTelefone = (telefone) => {
    const cleanTelefone = removeNonNumeric(telefone);
    if (cleanTelefone.length <= 2) return cleanTelefone;
    if (cleanTelefone.length <= 6) {
        return `(${cleanTelefone.slice(0, 2)}) ${cleanTelefone.slice(2)}`;
    }
    if (cleanTelefone.length === 10) {
        return `(${cleanTelefone.slice(0, 2)}) ${cleanTelefone.slice(2, 6)}-${cleanTelefone.slice(6)}`;
    }
    if (cleanTelefone.length === 11) {
        return `(${cleanTelefone.slice(0, 2)}) ${cleanTelefone.slice(2, 7)}-${cleanTelefone.slice(7)}`;
    }
    return cleanTelefone;
};

/**
 * Valida data de nascimento
 * @param {string} dataNascimento - Data no formato YYYY-MM-DD
 * @returns {{valid: boolean, message: string}}
 */
export const validateDataNascimento = (dataNascimento) => {
    if (!dataNascimento) return { valid: true, message: '' }; // Data é opcional
    
    const data = new Date(dataNascimento);
    const hoje = new Date();
    const idade = hoje.getFullYear() - data.getFullYear();
    const mes = hoje.getMonth() - data.getMonth();
    
    if (isNaN(data.getTime())) {
        return { valid: false, message: 'Data de nascimento inválida.' };
    }
    
    if (data > hoje) {
        return { valid: false, message: 'Data de nascimento não pode ser no futuro.' };
    }
    
    const idadeReal = mes < 0 || (mes === 0 && hoje.getDate() < data.getDate()) ? idade - 1 : idade;
    
    if (idadeReal > 150) {
        return { valid: false, message: 'Data de nascimento inválida. Idade muito alta.' };
    }
    
    if (idadeReal < 0) {
        return { valid: false, message: 'Data de nascimento não pode ser no futuro.' };
    }
    
    return { valid: true, message: '' };
};

/**
 * Valida estado (UF)
 * @param {string} estado - Estado a ser validado
 * @returns {{valid: boolean, message: string}}
 */
export const validateEstado = (estado) => {
    if (!estado) return { valid: true, message: '' }; // Estado é opcional
    
    const estadosValidos = [
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
        'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
        'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ];
    
    if (estado.length !== 2) {
        return { valid: false, message: 'Estado deve conter 2 letras (UF).' };
    }
    
    if (!estadosValidos.includes(estado.toUpperCase())) {
        return { valid: false, message: 'Estado inválido. Use a sigla da UF (ex: SP, RJ, MG).' };
    }
    
    return { valid: true, message: '' };
};

/**
 * Valida nome completo
 * @param {string} nome - Nome a ser validado
 * @returns {{valid: boolean, message: string}}
 */
export const validateNomeCompleto = (nome) => {
    if (!nome || typeof nome !== 'string') {
        return { valid: false, message: 'Nome completo é obrigatório.' };
    }
    
    const nomeTrimmed = nome.trim();
    
    if (nomeTrimmed.length === 0) {
        return { valid: false, message: 'Nome completo não pode estar vazio.' };
    }
    
    if (nomeTrimmed.length < 3) {
        return { valid: false, message: 'Nome completo deve conter pelo menos 3 caracteres.' };
    }
    
    if (nomeTrimmed.length > 200) {
        return { valid: false, message: 'Nome completo deve conter no máximo 200 caracteres.' };
    }
    
    // Verifica se contém pelo menos um espaço (nome e sobrenome)
    const partes = nomeTrimmed.split(/\s+/).filter(p => p.length > 0);
    if (partes.length < 2) {
        return { valid: false, message: 'Por favor, informe o nome completo (nome e sobrenome).' };
    }
    
    // Verifica se contém apenas letras, espaços e alguns caracteres especiais comuns
    if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(nomeTrimmed)) {
        return { valid: false, message: 'Nome completo contém caracteres inválidos. Use apenas letras, espaços e hífens.' };
    }
    
    return { valid: true, message: '' };
};

/**
 * Valida tamanho do arquivo de foto
 * @param {File} file - Arquivo a ser validado
 * @param {number} maxSizeMB - Tamanho máximo em MB (padrão: 5MB)
 * @returns {{valid: boolean, message: string}}
 */
export const validateFotoFile = (file, maxSizeMB = 5) => {
    if (!file) return { valid: true, message: '' }; // Foto é opcional
    
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    if (!file.type.startsWith('image/')) {
        return { valid: false, message: 'O arquivo deve ser uma imagem (JPG, PNG, etc.).' };
    }
    
    if (file.size > maxSizeBytes) {
        return { valid: false, message: `A imagem deve ter no máximo ${maxSizeMB}MB. Tamanho atual: ${(file.size / 1024 / 1024).toFixed(2)}MB.` };
    }
    
    return { valid: true, message: '' };
};
